import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Users,
  FileQuestion,
  Truck,
  Package,
  ArrowRight,
  TrendingUp,
  AlertTriangle,
  Clock,
  Settings as SettingsIcon
} from "lucide-react";
import { Link } from "react-router-dom";
import { useVendors } from "../hooks/useVendors";
import { useAcquisitions } from "../hooks/useAcquisitions";
import { usePOs } from "../hooks/usePOs";
import { useLogistics } from "../hooks/useLogistics";
import { useInventory } from "../hooks/useInventory";
import { getUserPreferences, saveUserPreferences } from "../services/userService";
import { ensureWeeklyTouchTasks } from "../services/tasksService";
import { db } from "../firebase/config";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import SortableWidget from "../components/ui/SortableWidget";
import DashboardSettingsModal from "../components/modals/DashboardSettingsModal";
import DelayedShippingModal from "../components/modals/DelayedShippingModal";
import TodayTasksWidget from "../components/dashboard/TodayTasksWidget";

const WIDGET_LABELS = {
  vendors: "Vendors Stat",
  pos: "Active POs Stat",
  logistics: "Logistics Stat",
  inventory: "Inventory Items Stat",
  today_tasks: "Today's Tasks",
  spend_analysis: "Purchase & Spend Analysis",
  alerts: "Stock & Operations Alerts",
  delayed_shipping: "Delayed Shipping"
};

export default function Dashboard() {
  const { currentUser } = useAuth();
  
  const { vendors } = useVendors();
  const { acqs } = useAcquisitions();
  const { pos } = usePOs();
  const { shipments } = useLogistics();
  const { inventory } = useInventory();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Drag and Drop state
  const [statsOrder, setStatsOrder] = useState(['vendors', 'pos', 'logistics', 'inventory']);
  const [summaryOrder, setSummaryOrder] = useState(['today_tasks', 'spend_analysis', 'alerts', 'delayed_shipping']);
  const [widgetVisibility, setWidgetVisibility] = useState({});
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState("");
  const [selectedDelayedPo, setSelectedDelayedPo] = useState(null);

  useEffect(() => {
    ensureWeeklyTouchTasks();
  }, []);

  useEffect(() => {
    if (!currentUser || !currentUser.email) return;

    const q = query(collection(db, "users"), where("email", "==", currentUser.email));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        setUserRole(data.role || "Admin"); // Default to Admin for testing if no role is set
        if (data.name) {
          setUserName(data.name);
        } else {
          setUserName(currentUser.email.split("@")[0]);
        }
      } else {
        setUserRole("Admin");
        setUserName(currentUser.email.split("@")[0]);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    async function loadPrefs() {
      if (!currentUser?.uid) return;
      try {
        const prefs = await getUserPreferences(currentUser.uid);
        if (prefs && prefs.dashboard) {
          if (prefs.dashboard.statsOrder) {
            const defaults = ['vendors', 'pos', 'logistics', 'inventory'];
            const loaded = prefs.dashboard.statsOrder;
            const merged = [...loaded, ...defaults.filter(w => !loaded.includes(w))];
            setStatsOrder(merged);
          }
          if (prefs.dashboard.summaryOrder) {
            const defaults = ['today_tasks', 'spend_analysis', 'alerts', 'delayed_shipping'];
            const loaded = prefs.dashboard.summaryOrder;
            const merged = [...loaded, ...defaults.filter(w => !loaded.includes(w))];
            setSummaryOrder(merged);
          }
          if (prefs.dashboard.widgetVisibility) setWidgetVisibility(prefs.dashboard.widgetVisibility);
        }
      } catch (err) {
        console.error("Failed to load user preferences", err);
      } finally {
        setPrefsLoaded(true);
      }
    }
    loadPrefs();
  }, [currentUser]);

  // Save prefs when state changes, but only after initial load
  useEffect(() => {
    if (!prefsLoaded || !currentUser?.uid) return;
    saveUserPreferences(currentUser.uid, {
      dashboard: {
        statsOrder,
        summaryOrder,
        widgetVisibility
      }
    });
  }, [statsOrder, summaryOrder, widgetVisibility, prefsLoaded, currentUser]);

  const stats = {
    vendors: (vendors || []).length || 12,
    pos: (pos || []).length || 8,
    shipments: (shipments || []).length || 5,
    inventory: (inventory || []).length || 142,
    lowStock: (inventory || []).filter(i => i && Number(i.quantity || 0) <= Number(i.minStock || 5)).length || 3,
    procurementTotal: (acqs || []).reduce((total, acq) => total + Number(acq?.totalCost || 0), 0) || 84600
  };

  const cardConfigMap = {
    vendors: {
      id: "vendors",
      title: "Vendors",
      value: stats.vendors,
      label: "Registered Vendors",
      icon: Users,
      color: "hsl(var(--hue-primary), 89%, 60%)",
      lightColor: "hsla(var(--hue-primary), 89%, 60%, 0.12)",
      path: "/vendors"
    },
    pos: {
      id: "pos",
      title: "Active POs",
      value: stats.pos,
      label: "Request for Quotes",
      icon: FileQuestion,
      color: "hsl(var(--hue-accent), 83%, 60%)",
      lightColor: "hsla(var(--hue-accent), 83%, 60%, 0.12)",
      path: "/po"
    },
    logistics: {
      id: "logistics",
      title: "Logistics",
      value: stats.shipments,
      label: "Active Shipments",
      icon: Truck,
      color: "hsl(var(--hue-success), 70%, 50%)",
      lightColor: "hsla(var(--hue-success), 70%, 50%, 0.12)",
      path: "/logistics"
    },
    inventory: {
      id: "inventory",
      title: "Inventory Items",
      value: stats.inventory,
      label: "Total SKUs tracked",
      icon: Package,
      color: "hsl(var(--hue-warning), 90%, 50%)",
      lightColor: "hsla(var(--hue-warning), 90%, 50%, 0.12)",
      path: "/inventory"
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (statsOrder.includes(active.id) && statsOrder.includes(over.id)) {
      setStatsOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    } else if (summaryOrder.includes(active.id) && summaryOrder.includes(over.id)) {
      setSummaryOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const widgetPermissions = {
    vendors: ["Procurement", "Manager", "Sales", "Admin"],
    pos: ["Procurement", "Manager", "Sales", "Admin"],
    inventory: ["Procurement", "Manager", "Sales", "Admin"],
    logistics: ["Warehousing", "Logistics", "Manager", "Admin"],
    alerts: ["Warehousing", "Logistics", "Manager", "Admin"],
    spend_analysis: ["Admin", "Manager"],
    delayed_shipping: ["Admin", "Manager", "Procurement"],
    today_tasks: ["Procurement", "Logistics", "Warehousing", "Admin", "Manager", "Business Development", "Sales", "Developer"]
  };

  const hasPermission = (widgetId) => {
    if (!userRole) return false; // Wait until role loads
    const allowedRoles = widgetPermissions[widgetId];
    if (!allowedRoles) return true; // Default to allow if no specific permissions
    return allowedRoles.includes(userRole) || userRole === "Admin";
  };

  const getDelayedPOs = () => {
    const intervalVal = parseInt(localStorage.getItem("global_delayed_shipping_interval") || "0", 10);
    const interval = isNaN(intervalVal) ? 0 : intervalVal;
    if (interval <= 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const delayedList = [];

    const safeShipments = shipments || [];
    const safePOs = pos || [];

    for (const shipment of safeShipments) {
      if (!shipment || !shipment.poId) continue;
      const po = safePOs.find(p => p && p.id === shipment.poId);
      if (!po || !po.items) continue;

      const delayedItems = [];

      for (const item of po.items) {
        if (!item || !item.id) continue;
        const itemStatus = shipment.itemStatuses?.[item.id]?.status || shipment.status || "To Order";
        if (itemStatus !== "Purchased") continue;

        const itemOrderedDate = shipment.itemStatuses?.[item.id]?.orderedDate || shipment.orderedDate;
        if (!itemOrderedDate) continue;

        const purchasedDate = new Date(itemOrderedDate);
        if (isNaN(purchasedDate.getTime())) continue; // Safe guard invalid date parsing
        purchasedDate.setHours(0, 0, 0, 0);
        const daysDiff = Math.floor((today - purchasedDate) / (1000 * 60 * 60 * 24));

        if (daysDiff >= interval) {
          delayedItems.push({
            ...item,
            purchasedDate: itemOrderedDate,
            daysDiff
          });
        }
      }

      if (delayedItems.length > 0) {
        delayedList.push({
          po,
          shipment,
          delayedItems
        });
      }
    }

    return delayedList;
  };

  const renderStatCard = (id) => {
    if (widgetVisibility[id] === false) return null;
    if (!hasPermission(id)) return null;
    
    const card = cardConfigMap[id];
    if (!card) return null;
    const Icon = card.icon;
    
    return (
      <SortableWidget key={id} id={id}>
        <div className="card stat-card" style={{ height: '100%' }}>
          <div>
            <span className="stat-label">{card.label}</span>
            <div className="stat-value">{card.value}</div>
          </div>
          <div
            className="stat-icon-wrapper"
            style={{ backgroundColor: card.lightColor, color: card.color }}
          >
            <Icon size={24} />
          </div>
        </div>
      </SortableWidget>
    );
  };

  const renderSummaryCard = (id) => {
    if (widgetVisibility[id] === false) return null;
    if (!hasPermission(id)) return null;

    if (id === 'today_tasks') {
      return (
        <SortableWidget key={id} id={id}>
          <TodayTasksWidget />
        </SortableWidget>
      );
    }
    
    if (id === 'spend_analysis') {
      return (
        <SortableWidget key={id} id={id}>
          <div className="card summary-card" style={{ height: '100%' }}>
            <div className="card-header-flex">
              <h3 className="card-title">
                <TrendingUp size={20} className="primary-icon" />
                Purchase & Spend Analysis
              </h3>
              <Link to="/procurement" className="card-link">
                View ACQs <ArrowRight size={16} />
              </Link>
            </div>
            <div className="spend-info-container">
              <div className="spend-value-group">
                <span className="spend-label">Total Spend Value (YTD)</span>
                <div className="spend-value">${stats.procurementTotal.toLocaleString()}</div>
              </div>
              <div className="spend-chart-placeholder">
                <div className="progress-bar-group">
                  <div className="progress-label-flex">
                    <span>Approved Contracts</span>
                    <span>72%</span>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: "72%", backgroundColor: "var(--primary)" }}></div>
                  </div>
                </div>
                <div className="progress-bar-group" style={{ marginTop: "1rem" }}>
                  <div className="progress-label-flex">
                    <span>Pending Invoices</span>
                    <span>28%</span>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: "28%", backgroundColor: "var(--accent)" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SortableWidget>
      );
    }

    if (id === 'alerts') {
      return (
        <SortableWidget key={id} id={id}>
          <div className="card summary-card" style={{ height: '100%' }}>
            <h3 className="card-title">
              <AlertTriangle size={20} className="warning-icon" />
              Stock & Operations Alerts
            </h3>
            <div className="alerts-container">
              {stats.lowStock > 0 ? (
                <div className="alert-item alert-danger-item">
                  <AlertTriangle size={20} />
                  <div>
                    <h4>Low Inventory Warning</h4>
                    <p>There are {stats.lowStock} items currently under minimum stock thresholds.</p>
                    <Link to="/inventory" className="alert-action-link">Restock Items</Link>
                  </div>
                </div>
              ) : (
                <div className="alert-item alert-success-item">
                  <AlertTriangle size={20} />
                  <div>
                    <h4>Inventory Optimal</h4>
                    <p>All warehouse stock levels are currently within safe thresholds.</p>
                  </div>
                </div>
              )}

              <div className="quick-actions-list">
                <h4>Quick Actions</h4>
                <div className="action-buttons-grid">
                  <Link to="/po" className="btn btn-secondary action-btn">
                    Create PO
                  </Link>
                  <Link to="/vendors" className="btn btn-secondary action-btn">
                    Add Vendor
                  </Link>
                  <Link to="/logistics" className="btn btn-secondary action-btn">
                    Track Shipment
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </SortableWidget>
      );
    }

    if (id === 'delayed_shipping') {
      const delayedPOs = getDelayedPOs();
      return (
        <SortableWidget key={id} id={id}>
          <div className="card summary-card" style={{ height: '100%' }}>
            <h3 className="card-title">
              <Clock size={20} className="warning-icon" />
              Delayed Shipping Alert
            </h3>
            <div className="alerts-container" style={{ marginTop: '1rem' }}>
              {delayedPOs.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {delayedPOs.map((d, index) => {
                    const maxDays = Math.max(...d.delayedItems.map(item => item.daysDiff));
                    return (
                      <div
                        key={index}
                        className="alert-item alert-danger-item"
                        style={{ cursor: 'pointer', margin: 0, padding: '1rem' }}
                        onClick={() => setSelectedDelayedPo(d)}
                      >
                        <Clock size={20} style={{ flexShrink: 0 }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>
                            {d.po.poNumber} {d.po.title ? `— ${d.po.title}` : ""}
                          </h4>
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', opacity: 0.85 }}>
                            {d.delayedItems.length} item{d.delayedItems.length > 1 ? 's' : ''} delayed by up to {maxDays} days past purchased.
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="alert-item alert-success-item" style={{ margin: 0, padding: '1rem' }}>
                  <Clock size={20} />
                  <div>
                    <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>All Shipments on Schedule</h4>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', opacity: 0.85 }}>No items exceed the delayed shipping interval settings.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SortableWidget>
      );
    }
    
    return null;
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>Welcome Back, {(userName || currentUser?.email?.split("@")[0] || "User").split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}</h1>
          <p>Here is your logistics and supply chain summary for today.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div className="system-time-badge">
            <span className="live-pulse"></span>
            <span>System Active</span>
          </div>
          <button 
            className="btn btn-secondary" 
            style={{ padding: '0.5rem' }} 
            onClick={() => setIsSettingsOpen(true)}
            title="Dashboard Settings"
          >
            <SettingsIcon size={20} />
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {/* Stats Grid */}
        <div className="stats-grid">
          <SortableContext items={statsOrder} strategy={rectSortingStrategy}>
            {statsOrder.map(id => renderStatCard(id))}
          </SortableContext>
        </div>

        {/* Main Dashboard Section */}
        <div className="grid-2">
          <SortableContext items={summaryOrder} strategy={rectSortingStrategy}>
            {summaryOrder.map(id => renderSummaryCard(id))}
          </SortableContext>
        </div>
      </DndContext>

      {isSettingsOpen && (
        <DashboardSettingsModal 
          onClose={() => setIsSettingsOpen(false)} 
          statsOrder={statsOrder}
          summaryOrder={summaryOrder}
          widgetVisibility={widgetVisibility}
          setWidgetVisibility={setWidgetVisibility}
          widgetLabels={WIDGET_LABELS}
          hasPermission={hasPermission}
        />
      )}

      {selectedDelayedPo && (
        <DelayedShippingModal
          po={selectedDelayedPo.po}
          shipment={selectedDelayedPo.shipment}
          delayedItems={selectedDelayedPo.delayedItems}
          onClose={() => setSelectedDelayedPo(null)}
        />
      )}
    </div>
  );
}
